import { UserAddress } from './../schemas/user_address';
import { inject, Injectable, signal } from '@angular/core';
import { User } from '../schemas/user';
import { directusAuthInstance } from '../libs/directus';
import { RequestsService } from '../utils/services/requests.service';
import { createItem, readItems, updateItem } from '@directus/sdk';
import { UserContact } from '../schemas/user_contact';
import { Locale } from '../schemas/locale';
import { Preference } from '../schemas/user_preference';
import { HandleErrorsService } from '../utils/services/handle-errors.service';
import { ThemeService } from '../utils/services/theme.service';
import { LanguageService } from '../utils/services/language.service';
import { Language } from '../schemas/language';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public user = signal<User | null>(null);
  public loadingUserData = signal<boolean>(false);
  public userContacts = signal<UserContact | null>(null);
  public userAddress = signal<UserAddress | null>(null);
  public preferences = signal<Preference | null>(null);

  private handleErrorsService = inject(HandleErrorsService);
  private themeService = inject(ThemeService);
  private languageService = inject(LanguageService);
  private requestsService = inject(RequestsService);
  directusClient = directusAuthInstance;

  constructor() {}

  async init() {
    this.loadingUserData.set(true);
    const loginState = localStorage.getItem('isLoggedIn') === 'true';
    if (loginState) {
      const user = await this.getUser();
      this.loadingUserData.set(false);
      return user;
    } else {
      this.loadingUserData.set(false);
      return null;
    }
  }

  async getUser() {
    // if (!this.loadingUserData()) this.loadingUserData.set(true);
    try {
      const user = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          readItems('user', {
            fields: ['*.*.*'],
            sort: 'date_created',
            limit: 1,
          }),
        ),
      );

      if (user.length === 0) {
        await this.prepareUserData(null);
        // if (!this.loadingUserData()) this.loadingUserData.set(false);
        return null;
      }

      return await this.prepareUserData(user[0] || null);

      // return user;
      // if (!this.loadingUserData()) this.loadingUserData.set(false);
    } catch (error) {
      await this.prepareUserData(null);
      this.loadingUserData.set(false);
      this.handleErrorsService.handleErrors(error);

      return null;
    }
  }

  async prepareUserData(user: any | null) {
    if (user === null) return;

    const { nationality, user_preference } = user;

    const userData: User = {
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      names: user.names,
      code: user.code,
      user_image: user.user_image,
      email: user.email,
      has_business: user.has_business,
      local: nationality as Locale,
      preference: user_preference as Preference,
    };

    this.user.set(userData);
    this.preferences.set(user_preference);
    // console.log('user_preference', user_preference);
    if (!this.preferences()) {
      await this.setPreferences(this.preferences()!);
    } else {
      await this.updatePreferences(this.preferences()!);
    }
    return userData;
  }

  async updateUser(user: User) {
    try {
      const userResponse = await this.requestsService.handleRequest(() =>
        this.directusClient.request(
          updateItem('user', this.user()!.id!, user),
          // updateItem('user', this.useres()![0].id!, user),
        ),
      );
      await this.getUser();
      return userResponse;
    } catch (error) {
      this.handleErrorsService.handleErrors(error);
    }
  }

  async setPreferences(preference: Preference) {
    const defaultLanguage = this.languageService.getAppLanguage();
    const defaultTheme = this.themeService.getCurrentTheme();
    let data: Preference | null = null;

    if (preference) {
      this.languageService.setAppLanguage(preference.language as Language);
      this.themeService.changeTheme(preference.theme!);
    }

    data = {
      theme: preference?.theme || defaultTheme,
      language:
        (preference?.language as Language)?.code || defaultLanguage?.code,
    };

    // console.log('Preferences', data);
    // Save User Preferences
    if (this.user() === null) return;
    return await this.requestsService.handleRequest(() =>
      this.directusClient.request(createItem('user_preference', data!)),
    );
  }

  async updatePreferences(preference: Preference) {
    const defaultLanguage = this.languageService.getAppLanguage();
    const defaultTheme = this.themeService.getCurrentTheme();
    this.languageService.setAppLanguage(preference.language as Language);
    this.themeService.changeTheme(preference.theme!);
    const preferenceUpdate = { ...preference, id: this.preferences()!.id! };
    this.preferences.set(preferenceUpdate);

    const data = {
      theme: preference?.theme || defaultTheme,
      language:
        (preference?.language as Language)?.code || defaultLanguage?.code,
    };
    // console.log('Preferences', data);
    // console.log('Other Preferences', this.preferences());
    if (
      data.language === (this.preferences()!.language as Language)?.code &&
      data.theme === this.preferences()!.theme
    )
      return;

    // console.log('Saving Update');

    // Save User Preferences
    if (this.user() === null) return;
    return await this.requestsService.handleRequest(() =>
      this.directusClient.request(
        updateItem('user_preference', this.preferences()!.id!, data),
      ),
    );
  }

  async toggleTheme() {
    this.themeService.toggleTheme();
    const defaultTheme = this.themeService.getCurrentTheme();

    const data = {
      theme: defaultTheme,
    };

    // Save User Preferences
    if (this.user() === null) return;
    return await this.requestsService.handleRequest(() =>
      this.directusClient.request(
        updateItem('user_preference', this.preferences()!.id!, data),
      ),
    );
  }

  async toggleLanguage(language: Language) {
    this.languageService.setAppLanguage(language);
    const defaultLanguage = this.languageService.getAppLanguage();

    const data = {
      language: defaultLanguage?.code,
    };

    // Save User Preferences
    if (this.user() === null) return;
    const languageResponse = await this.requestsService.handleRequest(() =>
      this.directusClient.request(
        updateItem('user_preference', this.preferences()!.id!, data),
      ),
    );

    location.reload();

    return languageResponse;
  }
}
